"use client";

import { useState, useEffect } from "react";
import { Quote, Star } from "lucide-react";
import { testimonials } from "@/data/homeData";
import { useLanguage } from "@/context/LanguageContext";
import React from "react";
import NextImage from "next/image";

interface Testimonial {
  id: string;
  author: string;
  position: string;
  content: string;
  avatar: string;
}

interface TestimonialCarouselProps {
  testimonials?: Testimonial[];
}

const TestimonialCarousel = React.memo(({ testimonials: propTestimonials }: TestimonialCarouselProps) => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerView = 3;
  
  const displayTestimonials = propTestimonials || testimonials;
  const maxSlide = Math.max(0, displayTestimonials.length - itemsPerView);

  useEffect(() => {
    if (maxSlide <= 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxSlide ? 0 : prev + 1));
    }, 4000);
    return () => clearInterval(interval);
  }, [maxSlide]);

  return (
    <div className="overflow-hidden relative w-full">
      <div
        className="flex transition-transform duration-1000 ease-in-out gap-8"
        style={{
          transform: `translateX(-calc(${currentIndex * (100 / itemsPerView)}% + ${currentIndex * 2}rem))`,
        }} // roughly calculate width + gap
      >
        {displayTestimonials.map((testi) => (
          <div
            key={testi.id}
            className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.33rem)] shrink-0 bg-[#040d21]/80 backdrop-blur-xl p-10 rounded-[2.5rem] shadow-[0_0_20px_rgba(0,0,0,0.5)] hover:shadow-[0_0_30px_rgba(0,243,255,0.1)] transition-all duration-500 border border-[#00f3ff]/20 relative group hover:-translate-y-1"
          >
            <Quote className="w-10 h-10 text-[#00f3ff]/10 absolute top-8 right-8" />
            <div className="flex gap-1 mb-6 text-[#00f3ff] drop-shadow-[0_0_3px_currentColor]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-current" />
              ))}
            </div>
            <p className="text-slate-300 font-medium italic mb-8 leading-relaxed line-clamp-4">
              &ldquo;{testi.content || t(`home.testimonials.items.${testi.id}`).content}&rdquo;
            </p>
            <div className="flex items-center gap-4 mt-auto">
              <NextImage
                alt={testi.author || t(`home.testimonials.items.${testi.id}`).author}
                className="w-14 h-14 rounded-full object-cover ring-4 ring-[#00f3ff]/20 group-hover:ring-[#00f3ff]/50 transition-all"
                src={testi.avatar || "/images/default-avatar.png"}
                width={56}
                height={56}
              />
              <div>
                <h4 className="font-bold text-white text-lg leading-tight group-hover:text-[#00f3ff] transition-all">
                  {testi.author || t(`home.testimonials.items.${testi.id}`).author}
                </h4>
                <p className="text-[#00f3ff] text-xs font-bold uppercase tracking-wider opacity-80">
                  {testi.position || t(`home.testimonials.items.${testi.id}`).position}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

TestimonialCarousel.displayName = "TestimonialCarousel";

export default TestimonialCarousel;
